'use client';

import { useMemo, useRef, useState } from 'react';
import { AlertTriangle, FileJson, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { TemplateExport } from '../lib/export-import-schema';
import { overwriteTemplate } from '../lib/export-import.service';
import { Badge } from '@/components/ui-kit/badge';
import { Input } from '@/components/ui-kit/input';
import { Button } from '@/components/ui-kit/button';

interface OverwriteImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string;
  templateSlug: string;
  currentScreenCount: number;
  currentFieldCount: number;
  onSuccess: () => Promise<void> | void;
}

export function OverwriteImportDialog({
  open,
  onOpenChange,
  templateId,
  templateSlug,
  currentScreenCount,
  currentFieldCount,
  onSuccess,
}: OverwriteImportDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<TemplateExport | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const importedScreenCount = preview?.template.screens.length ?? 0;
  const importedFieldCount = useMemo(
    () => preview?.template.screens.reduce((acc, screen) => acc + screen.fields.length, 0) ?? 0,
    [preview]
  );

  const slugMatches = preview ? preview.template.slug === templateSlug : false;
  const canOverwrite =
    !!preview && slugMatches && confirmChecked && confirmText === templateSlug && !isImporting;

  const resetState = () => {
    setFile(null);
    setPreview(null);
    setParseError(null);
    setIsDragging(false);
    setConfirmText('');
    setConfirmChecked(false);
    setIsImporting(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetState();
    }
    onOpenChange(nextOpen);
  };

  const processFile = async (selectedFile: File) => {
    setFile(selectedFile);
    setParseError(null);
    setPreview(null);

    try {
      const content = await selectedFile.text();
      const parsed = JSON.parse(content) as TemplateExport;

      if (!parsed.version || !parsed.template) {
        setParseError('Invalid export format. Missing required fields.');
        return;
      }

      setPreview(parsed);
    } catch {
      setParseError('Failed to parse JSON file. Please upload a valid export file.');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];

    if (!droppedFile) return;
    if (droppedFile.type === 'application/json' || droppedFile.name.endsWith('.json')) {
      processFile(droppedFile);
      return;
    }

    setParseError('Please drop a JSON file.');
  };

  const handleOverwrite = async () => {
    if (!preview) return;

    setIsImporting(true);
    try {
      const result = await overwriteTemplate(templateId, preview, confirmText);

      if (!result.success) {
        toast.error(result.message);
        setApiError(result.message);
        return;
      }

      toast.success(result.message);
      await onSuccess();
      handleOpenChange(false);
    } catch (error: any) {
      const message = error?.issues
        ? 'The imported JSON does not match the expected format.'
        : error instanceof Error
          ? error.message
          : 'An unexpected error occurred while overwriting.';
      toast.error(message);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-full bg-[hsl(var(--destructive))]/10">
              <AlertTriangle className="size-5 text-destructive" />
            </div>
            <DialogTitle>Overwrite Template from JSON</DialogTitle>
          </div>
          <DialogDescription>
            This replaces the template details, screens, and fields. The slug stays fixed as{' '}
            <code>{templateSlug}</code>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            className={cn(
              'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
              isDragging
                ? 'border-selise-blue bg-selise-blue/5'
                : 'border-border hover:border-[hsl(var(--selise-blue))]/50'
            )}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(false);
            }}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={(e) => {
                const selectedFile = e.target.files?.[0];
                if (selectedFile) processFile(selectedFile);
              }}
            />
            {file ? (
              <div className="flex items-center justify-center gap-2 text-[hsl(var(--fg))]">
                <FileJson className="size-5 text-selise-blue" />
                <span className="font-medium">{file.name}</span>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="size-8 mx-auto text-globe-grey" />
                <p className="text-sm text-globe-grey">
                  Drag and drop or click to select a JSON file
                </p>
              </div>
            )}
          </div>

          {parseError && (
            <div className="rounded-lg border border-[hsl(var(--destructive))]/20 bg-[hsl(var(--destructive))]/10 p-3 text-sm text-destructive">
              {parseError}
            </div>
          )}

          {preview && !parseError && (
            <div className="space-y-3 rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-globe-grey">Imported slug</span>
                <code
                  className={cn(
                    'px-2 py-0.5 rounded text-sm',
                    slugMatches
                      ? 'bg-lime-green/10 text-poly-green'
                      : 'bg-[hsl(var(--destructive))]/10 text-destructive'
                  )}
                >
                  {preview.template.slug}
                </code>
              </div>

              {!slugMatches && (
                <div className="rounded-lg border border-[hsl(var(--destructive))]/20 bg-[hsl(var(--destructive))]/10 p-3 text-sm text-destructive">
                  Slug mismatch: this template requires <code>{templateSlug}</code>. Overwrite is
                  blocked.
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg bg-muted p-3">
                  <p className="text-globe-grey mb-1">Current</p>
                  <div className="flex gap-2">
                    <Badge variant="secondary">{currentScreenCount} screens</Badge>
                    <Badge variant="secondary">{currentFieldCount} fields</Badge>
                  </div>
                </div>
                <div className="rounded-lg bg-muted p-3">
                  <p className="text-globe-grey mb-1">Import</p>
                  <div className="flex gap-2">
                    <Badge variant="secondary">{importedScreenCount} screens</Badge>
                    <Badge variant="secondary">{importedFieldCount} fields</Badge>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-[hsl(var(--destructive))]/20 bg-[hsl(var(--destructive))]/5 p-3 text-sm text-[hsl(var(--fg))]">
                <p className="font-medium mb-1">Destructive action</p>
                <p>
                  Existing screens and fields are deleted and replaced by this file. This cannot be
                  undone.
                </p>
              </div>

              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 rounded border-border"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                />
                <span>I understand this will permanently overwrite the current template.</span>
              </label>

              <div className="flex flex-col gap-2">
                <label className="text-sm text-globe-grey">
                  Type{' '}
                  <code className="bg-gray-100 text-pink-500 font-mono px-1.5 py-0.5 rounded">
                    {templateSlug}
                  </code>{' '}
                  to confirm
                </label>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value.trim())}
                  placeholder={templateSlug}
                />
              </div>
            </div>
          )}
          {apiError && (
            <div className="rounded-lg border border-[hsl(var(--destructive))]/20 bg-[hsl(var(--destructive))]/10 p-3 text-sm text-destructive">
              {apiError}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isImporting}>
            Cancel
          </Button>
          <Button onClick={handleOverwrite} disabled={!canOverwrite} variant={'destructive'}>
            {isImporting && <Loader2 className="size-4 animate-spin mr-2" />}
            {isImporting ? 'Overwriting...' : 'Overwrite Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
